import React, { useRef, useState, useEffect } from "react";
import { useDrop } from "react-dnd";
import { Document, Page, pdfjs } from "react-pdf";
import DraggableResizableField, { FieldData } from "./DraggableResizableField";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFDropZoneProps {
  pdfUrl: string;
  editFields: FieldData[];
  signFields: FieldData[];
  addField: (
    type: string,
    name: string | null,
    top: number,
    left: number,
    width: number,
    height: number,
    page: number,
    target?: string,
    signerId?: string,
  ) => void;
  editorMode: string;
  handleFieldDelete: (id: number, target: string) => void;
  handleFieldResize: (id: number, width: number, height: number, target: string) => void;
  handleUpdateFields: (
    id: number,
    type: string,
    name: string | null,
    top: number,
    left: number,
    width: number,
    height: number,
    page: number,
    target: string
  ) => void;
  setPdfDimensions: (dimensions: { width: number; height: number }) => void;
  pdfDimensions: { width: number; height: number };
  /** Visual zoom multiplier (1 = 100%). Coords stay in canonical (unzoomed) space. */
  zoom?: number;
}

const PDFDropZone: React.FC<PDFDropZoneProps> = ({
  pdfUrl,
  editFields,
  signFields,
  addField,
  editorMode,
  handleFieldDelete,
  handleFieldResize,
  handleUpdateFields,
  setPdfDimensions,
  zoom = 1,
}) => {
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(0);
  const [containerWidth, setContainerWidth] = useState(1000);
  const [droppedField, setDroppedField] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: "FIELD",
      drop: (item: any, monitor) => {
        // Use the cursor position (getClientOffset), not the dragged
        // source element's projected top-left (getSourceClientOffset). The
        // source is a small sidebar chip whose dimensions don't match the
        // dropped field — using its top-left makes the placed field jump
        // away from where the user actually released, and the gap looks
        // bigger the more the document is visually zoomed.
        const cursor = monitor.getClientOffset();
        if (cursor && pdfContainerRef.current) {
          const rect = pdfContainerRef.current.getBoundingClientRect();
          // rect is in zoomed CSS pixels — convert cursor offset to the
          // canonical (unzoomed) coord space so stored field positions
          // stay independent of the visual zoom level.
          const cursorX = (cursor.x - rect.left) / zoom;
          const cursorY = (cursor.y - rect.top) / zoom;
          const containerHeightUnzoomed = rect.height / zoom;
          const containerWidthUnzoomed = rect.width / zoom;

          let width = 200;
          let height = 100;
          if (item.type.toUpperCase() === "TEXT") {
            width = 200;
            height = 40;
          }

          // Pages are stacked with a 10 px gap (marginBottom on each page
          // wrapper). Compute the page from the per-slot height that
          // includes that gap, then express `top` relative to the page's
          // top edge (subtracting preceding pages' slots).
          const PAGE_GAP = 10;
          const pageSlot = numPages > 0 ? containerHeightUnzoomed / numPages : containerHeightUnzoomed;
          const pageIndex = Math.min(
            Math.max(Math.floor(cursorY / pageSlot), 0),
            Math.max(numPages - 1, 0),
          );
          const page = pageIndex + 1;
          const pageTopInContainer = pageIndex * pageSlot;

          // Center the field on the cursor for natural placement.
          const left = cursorX - width / 2;
          const top = cursorY - pageTopInContainer - height / 2;

          setDroppedField({
            type: item.type,
            name: item.name || item.type,
            width,
            height,
            // Clamp into the page so dropping near edges doesn't escape.
            top: Math.max(0, Math.min(top, pageSlot - PAGE_GAP - height)),
            left: Math.max(0, Math.min(left, containerWidthUnzoomed - width)),
            page,
            target: editorMode,
            signerId: item.signerId,
          });
        }
      },
      collect: (monitor) => ({ isOver: !!monitor.isOver() }),
    }),
    [editorMode, numPages, zoom]
  );

  // Apply the dropped field after rendering
  useEffect(() => {
    if (droppedField) {
      addField(
        droppedField.type,
        droppedField.name,
        droppedField.top,
        droppedField.left,
        droppedField.width,
        droppedField.height,
        droppedField.page,
        droppedField.target,
        droppedField.signerId,
      );
      setDroppedField(null);
    }
  }, [droppedField, addField]);

  const onDocumentLoad = async ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);

    if (!pdfUrl) return;

    try {
      const loadingTask = pdfjs.getDocument(pdfUrl);
      const pdf = await loadingTask.promise;

      if (!pdf) {
        console.error("PDF document is null");
        setLoading(false);
        return;
      }

      const firstPage = await pdf.getPage(1);

      if (!firstPage) {
        console.error("First page is null");
        setLoading(false);
        return;
      }

      const viewport = firstPage.getViewport({ scale: 2 });
      const { width, height } = viewport;

      setPdfDimensions({ width, height });
      setContainerWidth(width);
      setLoading(false);
    } catch (error) {
      console.error("Error loading PDF:", error);
      setLoading(false);
    }
  };

  const zoomedWidth = containerWidth * zoom;

  return (
    <div
      ref={drop as any}
      style={{
        flex: 1,
        overflow: "auto",
        position: "relative",
        width: `${zoomedWidth}px`,
        margin: "0 auto",
        backgroundColor: "#f0f0f0",
      }}
    >
      <div ref={pdfContainerRef} style={{ position: "relative" }}>
        <Document file={pdfUrl} onLoadSuccess={onDocumentLoad}>
          {loading ? (
            <div className="flex items-center justify-center h-screen">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
            </div>
          ) : (
            <>
              {[...Array(numPages)].map((_, index) => {
                const pageNumber = index + 1;
                return (
                  <div
                    key={pageNumber}
                    style={{
                      position: "relative",
                      width: `${zoomedWidth}px`,
                      // Scale the gap with zoom so a single canonical "slot"
                      // (page + gap) measures the same in unzoomed space at
                      // every zoom level. Otherwise drops near page edges
                      // shift by ~10px per zoom step.
                      marginBottom: `${10 * zoom}px`,
                    }}
                  >
                    <Page
                      pageNumber={pageNumber}
                      width={zoomedWidth}
                      renderAnnotationLayer={true}
                      renderTextLayer={true}
                    />

                    {/* Render sign fields only if in sign mode */}
                    {editorMode === "sign" &&
                      signFields.map((field) => {
                        if (field.page === pageNumber) {
                          return (
                            <DraggableResizableField
                              key={`sign-${field.id}`}
                              field={field}
                              zoom={zoom}
                              onDrag={(x, y) =>
                                handleUpdateFields(
                                  field.id,
                                  field.type,
                                  field.name,
                                  y,
                                  x,
                                  field.width,
                                  field.height,
                                  pageNumber,
                                  "sign"
                                )
                              }
                              onResize={(width, height) =>
                                handleFieldResize(field.id, width, height, "sign")
                              }
                              onDelete={() => handleFieldDelete(field.id, "sign")}
                            />
                          );
                        }
                        return null;
                      })}

                    {/* Render edit fields only if in edit mode */}
                    {editorMode === "edit" &&
                      editFields.map((field) => {
                        if (field.page === pageNumber) {
                          return (
                            <DraggableResizableField
                              key={`edit-${field.id}`}
                              field={field}
                              zoom={zoom}
                              fillMode={true}
                              onDrag={(x, y) =>
                                handleUpdateFields(
                                  field.id,
                                  field.type,
                                  field.name,
                                  y,
                                  x,
                                  field.width,
                                  field.height,
                                  pageNumber,
                                  "edit"
                                )
                              }
                              onResize={(width, height) =>
                                handleFieldResize(field.id, width, height, "edit")
                              }
                              onDelete={() => handleFieldDelete(field.id, "edit")}
                              onTextEdit={(text) => {
                                handleUpdateFields(
                                  field.id,
                                  field.type,
                                  text,
                                  field.top,
                                  field.left,
                                  field.width,
                                  field.height,
                                  pageNumber,
                                  "edit"
                                );
                              }}
                              editorMode="edit"
                            />
                          );
                        }
                        return null;
                      })}
                  </div>
                );
              })}
            </>
          )}
        </Document>
      </div>
    </div>
  );
};

export default PDFDropZone;
